<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2><i class="fas fa-user-tie me-2"></i>Mitarbeiter</h2>
      <span class="badge bg-secondary">{{ totalCount }} Einträge</span>
    </div>

    <!-- Single search across all firms -->
    <div class="search-bar mb-4">
      <div class="input-group">
        <span class="input-group-text"><i class="fas fa-search"></i></span>
        <input
            type="text"
            class="form-control"
            placeholder="Mitarbeiter durchsuchen …"
            v-model="searchQuery"
        >
        <button v-if="searchQuery" class="btn btn-outline-secondary" type="button" @click="searchQuery = ''">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- One section per firm/site -->
    <section v-for="group in groups" :key="group.key" class="firma-section mb-5">
      <header class="firma-head">
        <i class="fas fa-building me-2"></i>
        <h4 class="mb-0">{{ group.name }}</h4>
        <span class="badge bg-light text-dark ms-2">{{ group.employees.length }}</span>
      </header>

      <DataTable
          v-if="group.employees.length"
          :title="group.name"
          :columns="columns"
          :data="group.employees"
          :loading="loading"
          :total-pages="1"
          :total-count="group.employees.length"
          hide-search
          @sort="handleSort"
      >
        <template #name="{ item }">
          <div class="d-flex align-items-center gap-2">
            <user-avatar :src="item.icon_url" :seed="item.id" :name="item.name" :size="28" />
            <router-link :to="`/mitarbeiter/${item.id}`" class="text-decoration-none fw-bold">
              {{ item.name }}
            </router-link>
          </div>
        </template>

        <template #email="{ item }">
          <a v-if="item.email" :href="`mailto:${item.email}`" class="text-decoration-none">
            {{ item.email }}
          </a>
          <span v-else>-</span>
        </template>

        <template #role="{ item }">
          <span class="badge" :class="getRoleBadgeClass(item.role)">
            {{ getRoleText(item.role) }}
          </span>
        </template>

        <template #orders_count="{ item }">
          <router-link
              v-if="item.orders_count > 0"
              :to="{ path: '/auftraege', query: { employee_id: item.id } }"
              class="text-decoration-none"
          >
            {{ item.orders_count }}
          </router-link>
          <span v-else class="text-muted">0</span>
        </template>
      </DataTable>

      <div v-else class="firma-empty">
        <i class="fas fa-user-slash me-2"></i>
        <span v-if="searchQuery">Keine Treffer für diese Firma.</span>
        <span v-else>Noch keine aktiven Mitarbeiter für diese Firma hinterlegt.</span>
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import DataTable from '../components/DataTable.vue'
import UserAvatar from '../components/UserAvatar.vue'
import { roleLabel, roleBadgeClass } from '@/utils/roles'

export default {
  name: 'Mitarbeiter',
  components: { DataTable, UserAvatar },

  data() {
    return {
      allEmployees: [],
      // [{ key, name, count }] from the API (config/active_employees.yml).
      sites: [],
      searchQuery: '',
      sortField: 'name',
      sortDirection: 'asc',

      columns: [
        { key: 'name',         label: 'Name',      sortable: true },
        { key: 'email',        label: 'E-Mail',    sortable: true },
        { key: 'tel',          label: 'Telefon',   sortable: false },
        { key: 'mobil',        label: 'Mobil',     sortable: false },
        { key: 'abteilung',    label: 'Abteilung', sortable: true },
        { key: 'role',         label: 'Rolle',     sortable: true },
        { key: 'orders_count', label: 'Aufträge',  sortable: true }
      ]
    }
  },

  computed: {
    ...mapGetters(['loading']),

    totalCount() {
      return this.allEmployees.length
    },

    // Group employees into one list per configured firm/site, applying the
    // shared search + sort. Users with no site (config-missing fallback) land
    // in a trailing "Ohne Standort" group so nobody silently disappears.
    groups() {
      const q = this.searchQuery.trim().toLowerCase()
      const matches = (u) =>
        !q || [u.name, u.email, u.abteilung].some((f) => (f || '').toString().toLowerCase().includes(q))

      const sortFn = (a, b) => {
        const av = (a[this.sortField] ?? '').toString().toLowerCase()
        const bv = (b[this.sortField] ?? '').toString().toLowerCase()
        const cmp = av.localeCompare(bv, 'de')
        return this.sortDirection === 'asc' ? cmp : -cmp
      }

      const result = this.sites.map((s) => ({
        key: s.key,
        name: s.name,
        employees: this.allEmployees.filter((u) => u.site === s.key && matches(u)).sort(sortFn)
      }))

      const ungrouped = this.allEmployees.filter((u) => !u.site && matches(u)).sort(sortFn)
      if (ungrouped.length) {
        result.push({ key: '_none', name: 'Ohne Standort', employees: ungrouped })
      }
      return result
    }
  },

  mounted() {
    this.loadEmployees()
  },

  methods: {
    ...mapActions(['fetchData']),

    async loadEmployees() {
      try {
        const response = await this.fetchData({ endpoint: 'users', params: {} })
        this.allEmployees = response?.data || []
        this.sites = response?.meta?.sites || []
      } catch (error) {
        console.error('Error loading employees:', error)
        this.allEmployees = []
        this.sites = []
      }
    },

    getRoleText(role) {
      return roleLabel(role)
    },

    getRoleBadgeClass(role) {
      return roleBadgeClass(role)
    },

    handleSort({ field, direction }) {
      this.sortField = field
      this.sortDirection = direction
    }
  }
}
</script>

<style scoped>
.search-bar {
  max-width: 480px;
}

.firma-head {
  display: flex;
  align-items: center;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
  color: #374151;
}

.firma-empty {
  padding: 1.25rem;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  color: #6b7280;
}
</style>
